import * as React from 'react';
import * as _ from 'lodash';

/* Type Interfaces. */
import { IApplicationState } from '../../../../reducers';
import {
    Project,
    QTYPES,
    Question,
    QUOTA_STATUS,
    HealthProblemContext,
    QuestionSummary,
    OPTION_TYPES,
    MULTIMEDIA_TYPES,
    OPTION_SIZE,
    PROJECT_EDIT_STATUS,
    TrainedModelSummary
} from '@nexxt/common/types';
import { MediaSelectType } from '../../../libraries/mediaLibrary/MediaLibraryList';
import { alertMessage } from '../../../../constants/alertMessage';

/* Functions for redux dispatchers. */
import {
    saveUpdatedSortedQuestion,
    createQuestion,
    updateQuestion,
    cancelEditQuestion
} from '../../../../services/question.service';
import {
    setEditedStatus,
    clearEditedStatus
} from '../../../../services/utils.service';

/* Utility functions. */
import { getConfirmationBox } from '../../../common/swal/Sweetalert';
import {
    flattenQuestionSummary,
    getFilteredQuestionTitles
} from '../../../../utils/question';
import { sortHealthProblemByQuestionID } from '../../../../utils/project';
import { Notes } from '../../../common/notes';
import {
    ALERT_MESSAGE_TYPES,
    PROJECT_EDIT_MODES,
    TEMPLATE_QUESTION_SETTINGS
} from '../../../../interface/project.interface';
import { LOCK_PROJECT } from '../../../../constants/project';
import { createNewRichTextObject } from '@nexxt/common/services/TextService';
import * as uuid from 'uuid';
import AutoSavingService from '../../../../utils/AutoSavingService';
import ValidationService from '@nexxt/common/services/ValidationService';

export interface QuestionFormBaseProps {
    isAllowUserToEditAnswer: boolean;
    projectID?: number;
    editEnabled: boolean;
    trainedModelSummaries: TrainedModelSummary[];
    currentProject: Project;
    saveUpdatedSortedQuestion: (
        projectId: number,
        questionSummaries?: QuestionSummary[]
    ) => Promise<any>;
    createQuestion: (question: any, id: number) => void;
    editQuestion: (question: any) => void;
    toastNotification: any;
    currentQuestionId: number;
    currentQuestion: any;
    questionSummaries: QuestionSummary[];
    questionTitles: string[];
    projectHealth: any;
    editStatus: PROJECT_EDIT_STATUS;
    setEditedStatus: (
        type: string,
        status: PROJECT_EDIT_STATUS
    ) => Promise<any>;
    clearEditedStatus: (status?: PROJECT_EDIT_STATUS) => Promise<void>;
    updateQuestion: (question: any) => Promise<any>;
    cancelEditQuestion: () => Promise<any>;
    isEdited: boolean;
    isNewQuestion: boolean;
    showError: boolean;
    notify: any;
    history: any;
    location: any;
    viewMode: string;
    editMode: PROJECT_EDIT_MODES;
}

export interface QuestionFormBaseState {
    question: Question;
    showCardSettings: boolean;
    isSettingBoxLayoutOpen: boolean;
    isMediaLibraryOpen: boolean;
    showError: boolean;
    isSaving: boolean;
    questionError: any;
    isPipeOptionModalOpen: boolean;
    mediaType: MediaSelectType;
    isImageModalOpen: boolean;
    currentImage: string;
    templateSettings: TEMPLATE_QUESTION_SETTINGS;
    autoSavingService: AutoSavingService<
        | Question
        | {
              question: Question;
              selectedQuestions: QuestionSummary[];
          }
    >;
}

export class QuestionFormBase<
    P extends QuestionFormBaseProps,
    S extends QuestionFormBaseState
> extends React.Component<P, S> {
    private validationService;
    constructor(props: Readonly<P>, type: QTYPES = QTYPES.SINGLE_CHOICE) {
        super(props);
        let question: any = {};
        if (this.props.currentQuestionId) {
            question = this.props.currentQuestion;
        } else {
            question = this.getDefaultQuestion(
                this.props.projectID,
                type,
                props.currentQuestion.position,
                props.currentQuestion.parent
            ); // default values
        }
        this.validationService = new ValidationService('message');
        this.state = {
            isSettingBoxLayoutOpen: true,
            isMediaLibraryOpen: false,
            showCardSettings:
                question && question.options?.length > 0 ? true : false, // change this to another condition
            question: question,
            isSaving: false,
            showError: this.props.showError,
            questionError: {},
            isPipeOptionModalOpen: false,
            isImageModalOpen: false,
            currentImage: null,
            templateSettings: null,
            autoSavingService: new AutoSavingService(
                question,
                `question|${this.props.currentQuestionId || 'new'}`,
                false,
                this.props.editStatus,
                this.props.setEditedStatus,
                this.props.clearEditedStatus
            )
        } as S;
    }

    async componentDidMount(): Promise<void> {
        if (this.props.isNewQuestion) {
            await this.onSave();
        }
        this.getTemplateSettings();
        this.state.autoSavingService.setAutoSavingInterval(this.onSave);
        this.setEditFlag(true);
    }

    getValidationService = () => this.validationService;

    componentDidUpdate(prevProps: Readonly<P>, prevState: Readonly<S>): void {
        this.state.autoSavingService?.setData(this.state.question);

        if (!_.isEqual(prevProps.editStatus, this.props.editStatus)) {
            this.state.autoSavingService.setCurrentEditStatus(
                this.props.editStatus
            );
        }
        if (prevState.isSaving !== this.state.isSaving) {
            this.state.autoSavingService.setIsSaving(this.state.isSaving);
        }
    }
    componentWillUnmount(): void {
        this.state.autoSavingService &&
            this.state.autoSavingService.clearAutoSaving();
    }

    getUniqueQuestionLabel = (increment = 1) => {
        const questionLabel = `Unique Question Label ${increment}`;
        if (
            !this.props.questionTitles.find(
                (title) => title == questionLabel.toLowerCase()
            )
        ) {
            return questionLabel;
        }
        return this.getUniqueQuestionLabel(increment + 1);
    };
    /**
     * Get default question value by QTYPES
     * Need to update fields to be competitive with all question types
     */
    getDefaultQuestion = (
        projectID: number,
        type: QTYPES,
        position: number,
        parent: number
    ): Question => {
        return {
            id: 0,
            projectID,
            type,
            position,
            parent,
            continueTexts: [],
            title: this.getUniqueQuestionLabel(),
            showNotApplicableOption: false,
            showContinueButton: false,
            questionTexts: [],
            altTexts: [],
            notApplicableTexts: [],
            childrenIds: [],
            showFollowUp: false,
            followUpTexts: [],
            allowUserEdit: true,
            options: [],
            min: 1,
            max: 1,
            isRichText: false,
            selectedAlignOption: 'center',
            imageDisplay: 'square',
            selectedOptionType: OPTION_TYPES.TEXT_ONLY,
            random: false,
            optionsLayout: 1,
            pipingQuestions: [],
            piping: false,
            selectedRegion: {
                title: 'Canada',
                label: 'Canada',
                id: 'ca_lcc',
                value: 'ca_lcc',
                identifier: 'CA'
            },
            embeddedURL: '',
            playVideo: false,
            showImage: false,
            showText: false,
            completed: QUOTA_STATUS.TERMINATE,
            showTopics: false,
            showGenericProbe: false,
            lazyAnswer: false,
            gibberish: false,
            showAlternateWording: false,
            alternateWordingTexts: [],
            topics: [],
            genericProbeTexts: '',
            allowCopyPaste: false,
            followUpTextsDislike: [],
            followUpTextsLike: [],
            followUpTextsConfused: []
        };
    };

    getInitialQuestionField = (defaultValue, currentValue) => {
        return this.props.isNewQuestion ? defaultValue : currentValue;
    };
    //#region --- Component states comes here ---
    /**
     * Toggle Question Setting Box Show and Hide
     */
    toggleSettingBoxLayout = () => {
        this.setState({
            isSettingBoxLayoutOpen: !this.state.isSettingBoxLayoutOpen
        });
    };
    /**
     * Toggle Question Setting Box Show and Hide
     * if type is defined, set mediaType to show only proper meida from media library
     */
    toggleMediaLibrary = (type?: MediaSelectType) => () => {
        this.setState({
            isMediaLibraryOpen: true,
            mediaType: type || null
        });
    };

    closeMediaLibrary = () => {
        this.setState({
            isMediaLibraryOpen: false
        });
    };

    toggleImageModal = (image) => () => {
        this.setState({
            isImageModalOpen: !this.state.isImageModalOpen,
            currentImage: image
        });
    };
    /**
     * selectMedia function container
     * Function will define differently in each question type.
     */
    selectMedia = (selectedMedia: any, hasMultiMedia = false) => {
        if (this.state.mediaType === MediaSelectType.image && !hasMultiMedia) {
            this.setState(
                (prevState) => ({
                    question: {
                        ...(prevState.question as Question),
                        mediaURL: selectedMedia.src,
                        videoURL: null,
                        embeddedURL: null
                    },
                    isMediaLibraryOpen: false,
                    mediaType: null,
                    questionError: {
                        ...(this.state.questionError as any),
                        mediaURL: null
                    }
                }),
                this.setEditFlag
            );
        } else if (
            this.state.mediaType === MediaSelectType.image &&
            hasMultiMedia
        ) {
            const images = this.state.question.options || [];
            if (selectedMedia.length) {
                selectedMedia.forEach((media) => {
                    let index = images.length + 1;
                    images.push({
                        mediaURL: media,
                        label: '',
                        value: String(index++),
                        texts: [],
                        randomizeOption: true
                    });
                });
            } else {
                images.push({
                    mediaURL: selectedMedia.src,
                    label: selectedMedia.label,
                    value: String(images.length + 1),
                    texts: [],
                    randomizeOption: true
                });
            }

            this.setState(
                (prevState) => ({
                    question: {
                        ...(prevState.question as Question),
                        options: images,
                        mediaURL: null,
                        videoURL: null,
                        embeddedURL: null
                    },
                    isMediaLibraryOpen: false,
                    mediaType: null,
                    questionError: {
                        ...(this.state.questionError as any),
                        mediaURL: null
                    }
                }),
                this.setEditFlag
            );
        } else {
            this.setState(
                (prevState) => ({
                    question: {
                        ...(prevState.question as Question),
                        videoURL: selectedMedia.src,
                        embeddedURL: this.state.question.embeddedURL,
                        mediaURL: null
                    },
                    isMediaLibraryOpen: false,
                    mediaType: null,
                    questionError: {
                        ...(this.state.questionError as any),
                        videoURL: null
                    }
                }),
                this.setEditFlag
            );
        }
    };

    /**
     * Clear imbeded video
     */
    clearVideo = () => {
        this.setState(
            (prevState) => ({
                question: {
                    ...prevState.question,
                    videoURL: null,
                    embeddedURL: null
                },
                isMediaLibraryOpen: false,
                mediaType: null
            }),
            this.setEditFlag
        );
    };
    //#endregion

    //#region --- Question states comes here ---

    setQuestionAsQuestion = (question: Question) => {
        this.setState({ question }, this.setEditFlag);
    };

    /**
     * Update the single value in question state
     */
    setQuestion = (keyName: string, value: any) => {
        this.setState(
            (prevState) =>
                ({
                    question: {
                        ...(prevState.question as Question),
                        [keyName]: value
                    }
                } as Pick<QuestionFormBaseState, keyof QuestionFormBaseState>),
            this.setEditFlag
        );
    };

    /**
     * Update Rich text values in question state
     */
    setQuestionWithLanguage = (
        keyName: string,
        value: any,
        language: string
    ) => {
        const question = this.state.question;
        const currentObject = question[keyName] || [];
        let uploadedValue = false;

        const currentLanguage = currentObject.map((current) => {
            if (current.language === language) {
                uploadedValue = true;
                return { ...current, text: value };
            }
            return current;
        });
        if (!uploadedValue) {
            currentObject.push({ language, text: value });
            this.setState(
                (prevState) => ({
                    ...prevState,
                    question: {
                        ...(prevState.question as Question),
                        [keyName]: currentObject
                    }
                }),
                this.setEditFlag
            );
            return;
        }
        this.setState(
            (prevState) => ({
                ...prevState,
                question: {
                    ...(prevState.question as Question),
                    [keyName]: [...currentLanguage]
                }
            }),
            this.setEditFlag
        );
    };

    setQuestionTextTranslation = (
        keyName: string,
        value: any,
        language: string,
        index: number
    ) => {
        const question = this.state.question;
        const currentObject = question[keyName] || [];
        let uploadedValue = false;
        const newTextField = currentObject.filter(
            (current) => current.language === language
        )[0] || {
            language,
            richTexts: []
        };

        // newTextField.richTexts[index] = value;
        newTextField.richTexts = [
            ...newTextField?.richTexts.slice(0, index),
            value,
            ...newTextField.richTexts.slice(index + 1)
        ];

        const currentLanguage = currentObject.map((current) => {
            if (current.language === language) {
                uploadedValue = true;

                return { ...current, richTexts: newTextField.richTexts };
            }
            return current;
        });
        if (!uploadedValue) {
            currentObject.push(newTextField);
            this.setState(
                {
                    question: {
                        ...this.state.question,
                        [keyName]: currentObject
                    }
                },
                this.setEditFlag
            );
            return;
        }
        this.setState(
            {
                question: {
                    ...(this.state.question as Question),
                    [keyName]: [...currentLanguage]
                }
            },
            this.setEditFlag
        );
    };

    NEW_setQuestionTextTranslation = (
        keyName: string,
        value: any,
        language: string,
        index: number,
        type = MULTIMEDIA_TYPES.RICH_TEXT
    ): void => {
        const question = this.state.question;
        const currentObject = question[keyName] || [];
        let uploadedValue = false;
        let newFields = currentObject.filter(
            (current) => current.language === language
        )[0];

        switch (type) {
            case MULTIMEDIA_TYPES.RICH_TEXT:
                newFields = newFields || {
                    language,
                    contents: [{ type, richText: {}, id: uuid.v4() }]
                };
                newFields.contents = [
                    ...newFields.contents.slice(0, index),
                    { ...newFields.contents[index], type, richText: value },
                    ...newFields.contents.slice(index + 1)
                ];
                break;
            case MULTIMEDIA_TYPES.IMAGE:
            case MULTIMEDIA_TYPES.AUDIO:
            case MULTIMEDIA_TYPES.VIDEO:
                newFields.contents = [
                    ...newFields.contents.slice(0, index),
                    { ...newFields.contents[index], type, ...value },
                    ...newFields.contents.slice(index + 1)
                ];
                break;
            case MULTIMEDIA_TYPES.TEXT:
                newFields = newFields || {
                    language,
                    contents: [{ type, text: '' }]
                };
                newFields.contents = [
                    ...newFields.contents.slice(0, index),
                    { type, text: value },
                    ...newFields.contents.slice(index + 1)
                ];
                break;
        }

        const currentLanguage = currentObject.map((current) => {
            if (current.language === language) {
                uploadedValue = true;

                return { ...current, contents: newFields.contents };
            }
            return current;
        });

        if (!uploadedValue) {
            currentObject.push(newFields);
            this.setState(
                {
                    question: {
                        ...this.state.question,
                        [keyName]: currentObject
                    }
                },
                this.setEditFlag
            );
            return;
        }
        this.setState(
            {
                question: {
                    ...(this.state.question as Question),
                    [keyName]: [...currentLanguage]
                }
            },
            this.setEditFlag
        );
    };

    NEW_addNewEditor = (
        languageCode: string,
        position: number,
        type = MULTIMEDIA_TYPES.RICH_TEXT,
        data?: any
    ): void => {
        const questionTexts = this.state.question.questionTexts;
        const languageIndex = questionTexts.findIndex(
            (questionText) => questionText.language === languageCode
        );
        let newContent;
        if (languageIndex > -1) {
            switch (type) {
                case MULTIMEDIA_TYPES.RICH_TEXT:
                    newContent = {
                        id: uuid.v4(),
                        type,
                        richText: createNewRichTextObject('')
                    };

                    break;
                case MULTIMEDIA_TYPES.IMAGE:
                case MULTIMEDIA_TYPES.VIDEO:
                case MULTIMEDIA_TYPES.AUDIO:
                    newContent = {
                        id: uuid.v4(),
                        type,
                        mediaURL: data?.mediaURL,
                        mediaOptions: { size: OPTION_SIZE.MEDIUM },
                        answerReference: data?.answerReference
                    };
            }

            if (newContent) {
                questionTexts[languageIndex].contents = [
                    ...questionTexts[languageIndex].contents.slice(0, position),
                    newContent,
                    ...questionTexts[languageIndex].contents.slice(position)
                ];
            }
            this.setState(
                {
                    question: {
                        ...this.state.question,
                        questionTexts
                    }
                },
                this.setEditFlag
            );
        }
    };

    NEW_removeEditor = (languageCode: string, index: number) => {
        const questionTexts = this.state.question.questionTexts;
        const languageIndex = questionTexts.findIndex(
            (questionText) => questionText.language === languageCode
        );
        if (languageIndex > -1) {
            questionTexts[languageIndex].contents.splice(index, 1);

            this.setState(
                {
                    question: {
                        ...this.state.question,
                        questionTexts
                    }
                },
                () => {
                    this.setEditFlag();
                }
            );
        }
    };

    //#endregion

    //#region  --- Validation, save, cancel, etc. ---
    /**
     * Check whether question state has changed and show message besides lanuch button
     */
    setEditFlag = (init = false) => {
        const { autoSavingService } = this.state;
        !init && autoSavingService.setEditing();
        const checkValidity = this.validationService.validateByQuestionType(
            this.state.question,
            this.props.currentProject.languages,
            this.props.questionTitles || []
        );
        this.setState({
            questionError: checkValidity.isError
                ? checkValidity.errorObject
                : {}
        });

        autoSavingService.setEditFlag(checkValidity);
    };

    getTemplateSettings = () => {
        const { editMode, currentProject } = this.props;
        const { question } = this.state;
        try {
            const templateSettings = LOCK_PROJECT[
                editMode as PROJECT_EDIT_MODES
            ][currentProject?.templateCode]?.questions?.find(
                (q) => q.title === question.title
            );

            this.setState({ templateSettings });
        } catch (err) {}
    };

    renderErrorNotes = () => {
        const { projectHealth, currentQuestionId, showError } = this.props;
        return (
            showError &&
            projectHealth &&
            projectHealth[currentQuestionId] &&
            projectHealth[currentQuestionId].list.map((health, index) => (
                <Notes
                    key={`${health.questionRef}-${index}`}
                    note={health.message}
                    type={ALERT_MESSAGE_TYPES.WARN}
                />
            ))
        );
    };

    getWarningMessage = (): { type: string; message: string }[] => {
        const { question } = this.state;
        const { editEnabled } = this.props;
        switch (!editEnabled) {
            case question.isAudience && !!question.builtInID:
                return [
                    {
                        type: ALERT_MESSAGE_TYPES.WARN,
                        message: alertMessage.BUILT_IN_DISABLE_WARNING
                    }
                ];
            default:
                return [
                    {
                        type: ALERT_MESSAGE_TYPES.WARN,
                        message: alertMessage.GENERIC_DISABLE_WARNING
                    }
                ];
        }
    };

    syncQuestion = (updatedQuestion: Question, isError = false) => {
        const question = this.state.question;

        let optionIds = (updatedQuestion.options || [])
            ?.map((opt) => opt?.id)
            .filter((x) => x);
        let newOptions = [];

        if (question?.options?.length) {
            newOptions = question.options.map((opt, index) => {
                const referenceOption = updatedQuestion.options[index];
                optionIds = optionIds.filter((id) => id != referenceOption?.id);
                const newConditions = (opt.conditions || []).map(
                    (c, cIndex) => {
                        const referenceCondition =
                            referenceOption.conditions[cIndex];
                        const conditionID = referenceCondition?.conditionID;
                        const rules = c.rules.map((r, rIndex) => ({
                            ...r,
                            ruleID: referenceCondition.rules[rIndex]?.ruleID,
                            conditionID: conditionID,
                            new: false
                        }));

                        return { ...c, conditionID, rules, deletedRuleIDs: [] };
                    }
                );

                return {
                    ...opt,
                    id: referenceOption.id,
                    optionKey: referenceOption.optionKey,
                    conditions: newConditions || [],
                    deleteConditionIDs: []
                };
            });
        }
        let newQuestion = {
            ...question,
            deletedOptionIDs: optionIds,
            options: newOptions
        };
        if (question.type === QTYPES.POPUP) {
            const newPopupCard = question.popupCards.map((card, index) => {
                return {
                    ...card,
                    position: index + 1,
                    id: updatedQuestion.popupCards[index].id
                };
            });
            newQuestion = {
                ...newQuestion,
                childrenIds: updatedQuestion.childrenIds,
                popupCards: newPopupCard
            };
        }
        this.setState({ question: _.cloneDeep(newQuestion) }, () => {
            if (
                [
                    PROJECT_EDIT_STATUS.EDITED,
                    PROJECT_EDIT_STATUS.EDIT_REQUIRED
                ].includes(this.props.editStatus)
            ) {
                this.state.autoSavingService.setSyncedData(newQuestion);
                this.props.clearEditedStatus(
                    isError
                        ? PROJECT_EDIT_STATUS.EDIT_REQUIRED
                        : PROJECT_EDIT_STATUS.COMPLETED
                );
            }
        });

        this.setState({ isSaving: false });
    };
    /**
     * Save/Update the question value and call functions to pass the value to API
     */
    onSave = async () => {
        if (this.state.isSaving) {
            return;
        }
        const checkValidity = this.validationService.validateByQuestionType(
            this.state.question,
            this.props.currentProject.languages,
            this.props.questionTitles || []
        );
        this.setState({
            questionError: checkValidity.isError
                ? checkValidity.errorObject
                : {},
            isSaving: true
        });
        try {
            this.setState({
                isSaving: true
            });
            let data;
            if (this.props.currentQuestionId) {
                data = await this.props.updateQuestion(this.state.question);
                this.syncQuestion(data, checkValidity.isError);
            } else {
                data = await this.props.createQuestion(
                    this.state.question,
                    this.props.currentProject.id
                );
                this.setState(
                    {
                        isSaving: false,
                        question: _.cloneDeep(data)
                    },
                    () => {
                        this.state.autoSavingService.setSyncedData(data);
                        this.props.clearEditedStatus(
                            checkValidity.isError
                                ? PROJECT_EDIT_STATUS.EDIT_REQUIRED
                                : PROJECT_EDIT_STATUS.COMPLETED
                        );
                    }
                );
            }
        } catch (e) {
            this.setState({
                isSaving: false
            });
            console.log(e);
            this.props.clearEditedStatus(PROJECT_EDIT_STATUS.FAILED);
        }
    };

    /**
     * Alert message
     */
    cancelQuestion = () => {
        if (!this.state.isSaving) {
            if (this.props.isEdited) {
                getConfirmationBox({
                    message: alertMessage.CONFIRM_CONTINUE,
                    showCloseButton: true,
                    confirmButtonColor: '',
                    title: alertMessage.UNDO_CURRENT_CHANGE
                }).then((x) => {
                    if (x.value) {
                        this.props.cancelEditQuestion();
                    }
                });
            } else {
                this.props.cancelEditQuestion();
            }
        }
    };
}
//#endregion
export const mapStateToQuestionFormBaseProps = (store: IApplicationState) => {
    const {
        questionReducer: {
            isNewQuestion,
            currentQuestionId,
            currentQuestion,
            questionSummaries,
            showError
        },
        projectReducer: {
            currentProject,
            editEnabled,
            projectHealth,
            editMode
        },
        conversationalReducer: { trainedModelSummaries },
        utilsReducer: { isEdited, viewMode, editStatus }
    } = store;
    const questionTitles = flattenQuestionSummary(
        questionSummaries[currentProject.id]
    ).map((q) => (q.label || q.title)?.toLowerCase());
    return {
        editMode,
        viewMode,
        isNewQuestion,
        showError,
        currentProject,
        currentQuestionId,
        currentQuestion,
        questionSummaries: questionSummaries[currentProject.id],
        isEdited,
        editEnabled:
            // disable Dynata's built-in question input fields
            !(currentQuestion.isAudience && currentQuestion.builtInID) &&
            editEnabled['question'],
        projectHealth: sortHealthProblemByQuestionID(projectHealth, [
            HealthProblemContext.QUESTION
        ]),
        questionTitles: getFilteredQuestionTitles(
            questionTitles,
            currentQuestion
        ),
        trainedModelSummaries,
        editStatus
    };
};

export const mapDispatchToQuestionFormBaseProps = (dispatch) => ({
    createQuestion: (question: any, projectId: number) =>
        dispatch(createQuestion(question, projectId)),
    updateQuestion: (question: any) => dispatch(updateQuestion(question)),
    saveUpdatedSortedQuestion: (
        projectId: number,
        questionSummaries?: QuestionSummary[]
    ) => dispatch(saveUpdatedSortedQuestion(projectId, questionSummaries)),
    setEditedStatus: (type: string, status: PROJECT_EDIT_STATUS) =>
        dispatch(setEditedStatus(type, status)),
    clearEditedStatus: (status?: PROJECT_EDIT_STATUS) =>
        dispatch(clearEditedStatus(status)),
    cancelEditQuestion: () => dispatch(cancelEditQuestion())
});
